import { ExperienciaResumo, StatusCandidatura } from '@/types/candidatura'
import { EtapaComercial } from '@/types/statusReserva'

/**
 * Tela de Consultores (ADMIN) — espelha os DTOs do auth (`/v1/consultores`) e do
 * oportunidades (`/v1/oportunidades/consultores/indicadores`). Ver
 * `docs/PLANO_TELA_CONSULTORES.md` §6. A tela junta as duas fontes pelo `id` (sub do Keycloak).
 */

/**
 * Derivada no backend, não gravada: conta desabilitada = INATIVO (bloquear/inativar a
 * candidatura já desliga o acesso); habilitada com CONSULTOR = ATIVO; só com
 * CONSULTOR_ONBOARDING = EM_ONBOARDING.
 */
export type SituacaoConsultor = 'ATIVO' | 'EM_ONBOARDING' | 'INATIVO'

export const SITUACOES_CONSULTOR: SituacaoConsultor[] = ['ATIVO', 'EM_ONBOARDING', 'INATIVO']

export const ROTULO_SITUACAO_CONSULTOR: Record<SituacaoConsultor, string> = {
  ATIVO: 'Ativo',
  EM_ONBOARDING: 'Em onboarding',
  INATIVO: 'Inativo',
}

export const COR_SITUACAO_CONSULTOR: Record<SituacaoConsultor, 'success' | 'info' | 'default'> = {
  ATIVO: 'success',
  EM_ONBOARDING: 'info',
  INATIVO: 'default',
}

/** Linha da lista. Sem CPF nem banco; `statusCadastro`/`candidaturaId` nulos sem funil. */
export interface ConsultorResumo {
  id: string
  nome?: string | null
  email?: string | null
  celular?: string | null
  cnpj?: string | null
  tipoCanal?: string | null
  tenantId?: string | null
  tenantNome?: string | null
  situacao: SituacaoConsultor
  statusCadastro?: StatusCandidatura | null
  candidaturaId?: string | null
  criadoEm?: string | null
}

/** Contagens do topo — sobre todos os consultores, independente do filtro. */
export interface ConsultoresResumo {
  total: number
  ativos: number
  emOnboarding: number
  inativos: number
  semCandidatura: number
}

export type EtapaOnboarding = 'APRESENTACAO' | 'VIDEO_INSTITUCIONAL' | 'TUTORIAL' | 'PRODUTOS' | 'CONFIRMACAO'

/** Ficha do consultor. CPF, conta e chave PIX chegam MASCARADOS do backend. */
export interface ConsultorDetalhe {
  id: string
  nome?: string | null
  email?: string | null
  situacao: SituacaoConsultor
  contaHabilitada: boolean
  cpfMascarado?: string | null
  cnpj?: string | null
  tipoCanal?: string | null
  tenantId?: string | null
  tenantNome?: string | null
  dataNascimento?: string | null
  criadoEm?: string | null
  contato?: { celular?: string | null; telefone?: string | null; emailProfissional?: string | null } | null
  endereco?: {
    cep?: string | null
    logradouro?: string | null
    numero?: string | null
    complemento?: string | null
    bairro?: string | null
    cidade?: string | null
    uf?: string | null
  } | null
  dadosBancarios?: {
    banco?: string | null
    agencia?: string | null
    contaMascarada?: string | null
    tipoConta?: string | null
    chavePixMascarada?: string | null
  } | null
  /** Nula para conta criada direto pelo admin, sem passar pelo funil. */
  candidatura?: {
    id: string
    status: StatusCandidatura
    parecer?: string | null
    avaliadoEm?: string | null
    conviteDireto: boolean
    aceiteTermosEm?: string | null
    aceiteTermosVersao?: string | null
    formacao?: string | null
    segmentosExperiencia?: string | null
    mercadoOrigem?: string | null
    tiposEmpresaAtendidos?: string | null
    produtosComercializados?: string | null
    principalRelacionamento?: string | null
    regiaoAtuacao?: string | null
    resumoProfissional?: string | null
    experiencias?: ExperienciaResumo[] | null
    createdAt?: string | null
  } | null
  /** As 5 etapas da trilha do primeiro acesso; `concluidaEm` nulo = ainda não vista. */
  onboarding?: { etapa: EtapaOnboarding; titulo: string; concluidaEm?: string | null }[] | null
}

/**
 * Números de um consultor (uma linha por responsável). Consultor sem reserva NÃO vem —
 * a tela mostra zeros. Tudo calculado no backend com as regras do Dashboard.
 */
export interface IndicadoresConsultor {
  consultorId: string
  consultorNome?: string | null
  totalReservas: number
  porEtapa: Partial<Record<EtapaComercial, number>>
  emAndamento: number
  ganhas: number
  perdidas: number
  volumeEmAberto: number
  volumeFechado: number
  conversaoPercentual: number
  pendenciasAbertas: number
  ultimaReservaEm?: string | null
}

export interface ConsultoresFiltros {
  busca?: string
  situacao?: SituacaoConsultor | ''
  canal?: string
  page?: number
  size?: number
  /** `nome` (padrão), `criadoEm` ou `situacao`, com `,asc|desc`. */
  sort?: string
}
